import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api';
import './PlatformDashboard.css';

function PlatformDashboard() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await axiosInstance.get('/admin/platform-stats', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(res.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="pd-loading"><div className="pd-spinner" /></div>;
  if (error)   return <div className="pd-error">{t('PlatformDashboard.errorLoadingStats', { error })}</div>;

  const kpis = [
    { label: t('PlatformDashboard.kpiOrganisations'),        value: stats.totalOrganizations,   icon: 'fa-building',          color: '#3b82f6', path: '/adminHome/organisations' },
    { label: t('PlatformDashboard.kpiActiveUsers'),          value: stats.activeUsers,          icon: 'fa-users',             color: '#10b981', path: '/adminHome/usersManagement' },
    { label: t('PlatformDashboard.kpiPartners'),             value: stats.totalPartners,        icon: 'fa-network-wired',     color: '#8b5cf6', path: '/adminHome/partners' },
    { label: t('PlatformDashboard.kpiCisoResellers'),        value: stats.cisoPartners,         icon: 'fa-handshake',         color: '#f59e0b', path: '/adminHome/partners' },
    { label: t('PlatformDashboard.kpiCpaFirms'),             value: stats.cpaPartners,          icon: 'fa-balance-scale',     color: '#06b6d4', path: '/adminHome/partners' },
    { label: t('PlatformDashboard.kpiActiveSubscriptions'), value: stats.activeSubscriptions,  icon: 'fa-check-circle',      color: '#16a34a', path: '/adminHome/billing' },
    { label: t('PlatformDashboard.kpiRevenuePaid'),         value: `$${Number(stats.totalRevenuePaid || 0).toLocaleString()}`, icon: 'fa-dollar-sign', color: '#059669', path: '/adminHome/billing' },
    { label: t('PlatformDashboard.kpiPendingInvoices'),     value: stats.pendingInvoices,      icon: 'fa-file-invoice',      color: '#dc2626', path: '/adminHome/billing' },
    { label: t('PlatformDashboard.kpiDiscoveredIdentities'),value: stats.discoveredIdentities, icon: 'fa-id-card',           color: '#7c3aed', path: '/adminHome/asm' },
    { label: t('PlatformDashboard.kpiPendingInvitations'),  value: stats.pendingInvitations,   icon: 'fa-envelope-open-text',color: '#0ea5e9', path: '/adminHome/usersManagement' },
  ];

  const breakdown = stats.orgSubscriptionBreakdown || [];

  return (
    <div className="pd-page">
      {/* KPI Grid */}
      <div className="pd-kpi-grid">
        {kpis.map(k => (
          <div
            key={k.label}
            className="pd-kpi-card"
            onClick={() => navigate(k.path)}
            style={{ '--kpi-color': k.color }}
          >
            <div className="pd-kpi-icon" style={{ color: k.color, background: `${k.color}18` }}>
              <i className={`fas ${k.icon}`} />
            </div>
            <div className="pd-kpi-body">
              <div className="pd-kpi-value">{k.value ?? 0}</div>
              <div className="pd-kpi-label">{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Subscription Breakdown */}
      <section className="pd-section">
        <h2 className="pd-section-title">{t('PlatformDashboard.subscriptionPlanBreakdown')}</h2>
        {breakdown.length === 0 ? (
          <p className="pd-empty">{t('PlatformDashboard.noSubscriptionPlans')}</p>
        ) : (
          <table className="pd-table">
            <thead>
              <tr>
                <th>{t('PlatformDashboard.tablePlan')}</th>
                <th>{t('PlatformDashboard.tableTier')}</th>
                <th>{t('PlatformDashboard.tableActiveOrgs')}</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((row, i) => (
                <tr key={i}>
                  <td>{row.name}</td>
                  <td>
                    <span className={`pd-tier-badge pd-tier-${(row.tier || '').toLowerCase()}`}>
                      {row.tier}
                    </span>
                  </td>
                  <td className="pd-count">{row.org_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Quick Actions */}
      <section className="pd-section">
        <h2 className="pd-section-title">{t('PlatformDashboard.quickActions')}</h2>
        <div className="pd-actions">
          <button className="pd-action-btn" onClick={() => navigate('/adminHome/organisations')}>
            <i className="fas fa-building" /> {t('PlatformDashboard.manageOrgs')}
          </button>
          <button className="pd-action-btn" onClick={() => navigate('/adminHome/partners')}>
            <i className="fas fa-network-wired" /> {t('PlatformDashboard.managePartners')}
          </button>
          <button className="pd-action-btn" onClick={() => navigate('/adminHome/usersManagement')}>
            <i className="fas fa-users-cog" /> {t('PlatformDashboard.manageUsers')}
          </button>
          <button className="pd-action-btn" onClick={() => navigate('/adminHome/billing')}>
            <i className="fas fa-credit-card" /> {t('PlatformDashboard.billing')}
          </button>
        </div>
      </section>
    </div>
  );
}

export default PlatformDashboard;
